import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, FileText, Twitter, Linkedin, Video, Sparkles, Award, Zap, Globe, BarChart } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface ContentTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedContentTypes: string[];
  onContentTypeSelect: (type: string) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  topicTitle: string;
}

export const ContentTypeDialog = ({
  open,
  onOpenChange,
  selectedContentTypes,
  onContentTypeSelect,
  onGenerate,
  isGenerating,
  topicTitle,
}: ContentTypeDialogProps) => {
  const handleGenerateClick = () => {
    if (selectedContentTypes.length === 0) return;
    onGenerate();
  };

  // Enhanced content type definitions with icons and detailed benefits in bullet points
  const contentTypes = [
    {
      id: "blog",
      name: "Blog Post",
      icon: <FileText className="h-8 w-8 text-blue-500 dark:text-blue-400" />,
      benefits: [
        "SEO optimized for higher rankings",
        "Long-form comprehensive content",
        "Engaging sections with proper structure"
      ],
      color: "blue",
      bgClass: "bg-blue-50 dark:bg-blue-950/30",
      borderClass: "border-blue-300 dark:border-blue-700",
      ringClass: "ring-blue-200 dark:ring-blue-700",
      bgSelectedClass: "bg-blue-500 dark:bg-blue-600",
      checkboxClass: "data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 dark:data-[state=checked]:bg-blue-600 dark:data-[state=checked]:border-blue-600"
    },
    {
      id: "twitter",
      name: "Twitter Post",
      icon: <Twitter className="h-8 w-8 text-sky-500 dark:text-sky-400" />,
      benefits: [
        "Perfect character count for impact",
        "Virality factor enhanced",
        "Engaging hooks for more retweets"
      ],
      color: "sky",
      bgClass: "bg-sky-50 dark:bg-sky-950/30",
      borderClass: "border-sky-300 dark:border-sky-700",
      ringClass: "ring-sky-200 dark:ring-sky-700",
      bgSelectedClass: "bg-sky-500 dark:bg-sky-600",
      checkboxClass: "data-[state=checked]:bg-sky-500 data-[state=checked]:border-sky-500 dark:data-[state=checked]:bg-sky-600 dark:data-[state=checked]:border-sky-600"
    },
    {
      id: "linkedin",
      name: "LinkedIn Post",
      icon: <Linkedin className="h-8 w-8 text-blue-500 dark:text-blue-400" />,
      benefits: [
        "Professional tone for business audience",
        "Strategic format for maximum visibility",
        "Industry-relevant insights and trends"
      ],
      color: "indigo",
      bgClass: "bg-blue-50 dark:bg-blue-950/30",
      borderClass: "border-blue-300 dark:border-blue-700",
      ringClass: "ring-blue-200 dark:ring-blue-700",
      bgSelectedClass: "bg-blue-500 dark:bg-blue-600",
      checkboxClass: "data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 dark:data-[state=checked]:bg-blue-600 dark:data-[state=checked]:border-blue-600"
    },
    {
      id: "video",
      name: "Video Script",
      icon: <Video className="h-8 w-8 text-red-500 dark:text-red-400" />,
      benefits: [
        "Structured for maximum viewer retention",
        "Compelling hooks and storytelling",
        "Clear call-to-actions for engagement"
      ],
      color: "red",
      bgClass: "bg-red-50 dark:bg-red-950/30",
      borderClass: "border-red-300 dark:border-red-700",
      ringClass: "ring-red-200 dark:ring-red-700",
      bgSelectedClass: "bg-red-500 dark:bg-red-600",
      checkboxClass: "data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500 dark:data-[state=checked]:bg-red-600 dark:data-[state=checked]:border-red-600"
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px] md:max-w-[750px] lg:max-w-[900px] p-0 overflow-hidden bg-white dark:bg-zinc-800 rounded-xl">
        <div className="bg-gradient-to-r from-blue-50 to-blue-50 dark:from-zinc-800/80 dark:to-blue-950/30 p-6 border-b border-zinc-200 dark:border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              Generate Content
            </DialogTitle>
            <DialogDescription className="text-zinc-600 dark:text-zinc-400 mt-1">
              Select content types to generate for <span className="font-medium text-zinc-800 dark:text-zinc-300">"{topicTitle}"</span>
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contentTypes.map((type) => {
              const isSelected = selectedContentTypes.includes(type.id);
              
              return (
                <div 
                  key={type.id}
                  className={cn(
                    "relative rounded-xl border transition-all cursor-pointer flex flex-col overflow-hidden",
                    isSelected 
                      ? `${type.borderClass} ${type.bgClass} ring-2 ${type.ringClass} shadow-md` 
                      : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 hover:bg-zinc-50/50 dark:hover:bg-zinc-700/50 hover:shadow-sm"
                  )}
                  onClick={() => onContentTypeSelect(type.id)}
                >
                  <div className={cn("p-4 flex items-center gap-3 border-b", 
                    isSelected ? "border-zinc-200/50 dark:border-zinc-700/50" : "border-zinc-100 dark:border-zinc-700"
                  )}>
                    <div className={cn("p-2 rounded-lg", 
                      isSelected ? `${type.bgClass}/80` : "bg-zinc-100 dark:bg-zinc-700"
                    )}>
                      {type.icon}
                    </div>
                    <div>
                      <Label
                        htmlFor={type.id}
                        className="text-lg font-medium text-zinc-800 dark:text-zinc-200"
                      >
                        {type.name}
                      </Label>
                    </div>
                  </div>
                  
                  <div className="p-4 flex-1 flex flex-col">
                    <ul className="space-y-1.5 text-sm text-zinc-600 dark:text-zinc-300">
                      {type.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className={cn("mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full", 
                            isSelected ? (type.id === "blog" ? "bg-blue-500 dark:bg-blue-400" : 
                                        type.id === "twitter" ? "bg-sky-500 dark:bg-sky-400" : 
                                        type.id === "linkedin" ? "bg-blue-500 dark:bg-blue-400" : 
                                        "bg-red-500 dark:bg-red-400") : "bg-zinc-400 dark:bg-zinc-500"
                          )}></span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
          
          {selectedContentTypes.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  <Award className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    {selectedContentTypes.length} content {selectedContentTypes.length === 1 ? 'type' : 'types'} selected
                  </h3>
                  <div className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                    <p>
                      We'll generate high-quality, AI-optimized content tailored to your topic and audience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="bg-zinc-50 dark:bg-zinc-900 p-6 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateClick}
            disabled={selectedContentTypes.length === 0 || isGenerating}
            className={cn(
              "w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-200",
              selectedContentTypes.length === 0 && "opacity-50 cursor-not-allowed"
            )}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Zap className="mr-2 h-4 w-4" />
                Generate Content
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
